import { useCallback } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useInactivityTimeout } from "../../hooks/useInactivityTimeout";
import { SessionTimeoutWarning } from "../SessionTimeoutWarning/SessionTimeoutWarning";

export function ProtectedRoute({ children }) {
  const { authToken, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleTimeout = useCallback(() => {
    // Navigate first so the inactivity URL is in place before logout()
    // clears the auth token and re-renders this guard.
    navigate("/login?reason=inactivity", { replace: true });
    logout();
  }, [logout, navigate]);

  const { showWarning, remainingMs, extendSession } =
    useInactivityTimeout(handleTimeout);

  if (!authToken) {
    // Preserve the current search string so redirect reasons (e.g. reason=inactivity)
    // survive the guard. LoginPage already reads qParams.get("next") — no changes there.
    return <Navigate to={`/login${location.search}`} replace />;
  }

  // Layout-route: no children prop → render nested routes via <Outlet>
  return (
    <>
      {children ?? <Outlet />}
      <SessionTimeoutWarning
        open={showWarning}
        remainingMs={remainingMs}
        onExtend={extendSession}
        onLogout={handleTimeout}
      />
    </>
  );
}
